import { Transform, Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsIn,
  IsInt,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import { INGREDIENT_PROVENANCES, type IngredientProvenance } from './ingredient-provenance.enum';

/**
 * Curation state of a catalog row: `'never'` = `curatedAt IS NULL` (no
 * platform admin has ever touched it), `'done'` = `curatedAt IS NOT NULL`.
 */
export const INGREDIENT_CURATION_STATES = ['never', 'done'] as const;
export type IngredientCurationState = (typeof INGREDIENT_CURATION_STATES)[number];

/**
 * Which catalog fields a curator can flag as missing on a row. Combined with
 * AND semantics (`missing=allergens,density` keeps only rows missing BOTH),
 * matching the "find the incomplete rows" curation job (see
 * `.context/admin-ingredients-filters-spec.md`).
 */
export const INGREDIENT_MISSING_DATA_FLAGS = [
  'allergens',
  'compound',
  'ediblePortion',
  'density',
] as const;
export type IngredientMissingDataFlag = (typeof INGREDIENT_MISSING_DATA_FLAGS)[number];

/**
 * Which slice of the tenant-visible union (own rows + global catalog) to
 * return. `'all'` is the default — the same union `GET /ingredients` always
 * returned before pagination existed. `'mine'`/`'global'` back the two UI
 * groups ("I miei ingredienti" / "Catalogo Prospero") server-side, so the
 * frontend never has to hold the whole list in memory to split it.
 */
export const INGREDIENT_ORIGIN_VALUES = ['mine', 'global', 'all'] as const;
export type IngredientOrigin = (typeof INGREDIENT_ORIGIN_VALUES)[number];

/**
 * `curatedAt` is only ever set by `AdminIngredientController` on a global row
 * (`tenant_id IS NULL`) — on the tenant-scoped `GET /ingredients` it is NULL
 * for every row this endpoint's `origin=mine` slice can return, so sorting
 * by it there only orders the `global` slice meaningfully. Kept in the same
 * whitelist as `name`/`createdAt` rather than split per-controller because
 * both controllers share `buildIngredientOrderBy`.
 */
export const INGREDIENT_SORT_COLUMNS = ['name', 'createdAt', 'curatedAt'] as const;
export type IngredientSortColumn = (typeof INGREDIENT_SORT_COLUMNS)[number];

/**
 * Shared by `GET /ingredients` (tenant-scoped, `origin` meaningful) and
 * `GET /admin/ingredients` (global-catalog-only, `origin` is simply unused
 * there since every row is already global) — search/sort/paginate is the same
 * shape either way, and duplicating it per controller would only invite the
 * two copies to drift.
 */
export class GetIngredientsQueryDto {
  /** Case-insensitive substring match against `name`, any `synonyms` entry,
   * `brand`, or `externalCode` — a curator searches by listing code
   * ("00132") or by brand ("Agrimontana") just as often as by name. */
  @IsOptional()
  @IsString()
  q?: string;

  @IsOptional()
  @IsIn(INGREDIENT_ORIGIN_VALUES)
  origin?: IngredientOrigin;

  /**
   * Keeps only the ingredients declaring at least one of these allergens
   * (either `CONTAINS` or `MAY_CONTAIN` — a "puo' contenere" glutine is still
   * a gluten hit for whoever is filtering). Serialized comma-separated
   * (`?allergenIds=a,b`) because the frontend builds the query string with
   * `URLSearchParams.set`, one key per param.
   */
  @IsOptional()
  @Transform(({ value }) =>
    typeof value === 'string' ? value.split(',').filter((id) => id.length > 0) : value,
  )
  @IsArray()
  @IsUUID('all', { each: true })
  allergenIds?: string[];

  /**
   * Keeps the ingredients with no allergen declaration at all. Combined with
   * `allergenIds` it widens (OR) rather than narrows: "senza allergeni oppure
   * con glutine". The UI keeps the two mutually exclusive, but the endpoint
   * stays defined for any caller that sends both.
   */
  @IsOptional()
  @Transform(({ value }) => value === true || value === 'true')
  @IsBoolean()
  withoutAllergens?: boolean;

  /**
   * Keeps only the ingredients with at least one `ingredient_allergens` row
   * whose `source` is `'AI'` — i.e. an allergen inferred by the AI pipeline
   * rather than declared by a user/parser/table. Not a "missing data" flag
   * (semantics differ from `missing`): this one flags data that IS present
   * but was AI-inferred and may still need curator confirmation.
   */
  @IsOptional()
  @Transform(({ value }) => value === true || value === 'true')
  @IsBoolean()
  aiInferredAllergens?: boolean;

  /**
   * Curation-only filters below (`catalogSource`, `brand`, `provenance`,
   * `curation`, `missing`): meaningful only against the global catalog.
   * `GET /admin/ingredients` is global-only by construction, so they apply
   * directly there. On the tenant-scoped `GET /ingredients` they are still
   * accepted (same shared DTO) but only ever select rows inside the
   * `origin=global` slice of the tenant-visible union — a tenant's own rows
   * never carry a `catalogSource`/`brand`/curation state, so combining one
   * of these with `origin=mine` (or the default `origin=all` alongside a
   * tenant row) simply yields no match from that side of the union.
   */
  @IsOptional()
  @IsString()
  catalogSource?: string;

  @IsOptional()
  @IsString()
  brand?: string;

  @IsOptional()
  @IsIn(INGREDIENT_PROVENANCES)
  provenance?: IngredientProvenance;

  @IsOptional()
  @IsIn(INGREDIENT_CURATION_STATES)
  curation?: IngredientCurationState;

  /** Serialized comma-separated (`?missing=allergens,density`), same
   * convention as `allergenIds`. AND semantics: a row must be missing EVERY
   * selected field to match. */
  @IsOptional()
  @Transform(({ value }) =>
    typeof value === 'string' ? value.split(',').filter((flag) => flag.length > 0) : value,
  )
  @IsArray()
  @IsIn(INGREDIENT_MISSING_DATA_FLAGS, { each: true })
  missing?: IngredientMissingDataFlag[];

  @IsOptional()
  @IsIn(INGREDIENT_SORT_COLUMNS)
  sortBy?: IngredientSortColumn;

  @IsOptional()
  @IsIn(['asc', 'desc'])
  sortDir?: 'asc' | 'desc';

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(100)
  pageSize?: number;
}
